import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Stack } from '@/components/ui/layout';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput } from '@/components/ui/inputs';
import { Alert } from '@/components/ui/feedback';
import { IconMail, IconArrowLeft, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { FadeIn, AnimatedFormWrapper } from '@/components/animations';
import { useHttpClient } from '@actschurch/ccms-sdk/react';
import { validators } from '@/utils/validation';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState('');
  const [countdown, setCountdown] = useState(0);

  const httpClient = useHttpClient();

  const validate = (): boolean => {
    const emailValidation = validators.email(email);
    if (emailValidation !== true) {
      setEmailError(emailValidation);
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!validate()) {
      return;
    }

    setIsLoading(true);

    try {
      await httpClient.post('/users/api/auth/forgot-password', { email, appUrl: window.location.origin });
      setSuccess(true);

      // Start countdown timer
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      setApiError(err.response?.data?.message || err.message || 'Failed to send reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;

    setSuccess(false);
    setApiError('');
    await handleSubmit(new Event('submit') as any);
  };

  return (
    <Stack gap="lg">
      {/* Back Button */}
      <FadeIn delay={200}>
        <Button
          component={Link}
          to="/auth/login"
          variant="subtle"
          size="xs"
          leftSection={<IconArrowLeft size={14} />}
          w="fit-content"
        >
          Back to login
        </Button>
      </FadeIn>

      {/* Header */}
      <FadeIn delay={300}>
        <Stack align="center" gap="xs">
          <Badge
            variant="light"
            color="indigo"
            size="lg"
          >
            Password Reset
          </Badge>
          <Title order={2} ta="center" mt="md">
            Forgot your password?
          </Title>
          <Text c="dimmed" size="sm" ta="center">
            Enter your email address and we'll send you a link to reset your password
          </Text>
        </Stack>
      </FadeIn>

      {/* Success Alert */}
      {success && (
        <FadeIn>
          <Alert
            icon={<IconCheck size={16} />}
            title="Email Sent"
            color="green"
          >
            <Stack gap="xs">
              <Text size="sm">
                Password reset link has been sent to <strong>{email}</strong>.
                Please check your inbox and follow the instructions.
              </Text>
              {countdown > 0 ? (
                <Text size="xs" c="dimmed">
                  You can request a new link in {countdown} seconds
                </Text>
              ) : (
                <Anchor size="sm" onClick={handleResend} style={{ cursor: 'pointer' }}>
                  Didn't receive it? Resend email
                </Anchor>
              )}
            </Stack>
          </Alert>
        </FadeIn>
      )}

      {/* Error Alert */}
      {apiError && (
        <FadeIn>
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
          >
            {apiError}
          </Alert>
        </FadeIn>
      )}

      {/* Form */}
      {!success && (
        <form onSubmit={handleSubmit}>
          <AnimatedFormWrapper animation="stagger" delay={400} staggerDelay={100}>
            <TextInput
              label="Email Address"
              placeholder="your@email.com"
              leftSection={<IconMail size={16} />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
              required
              size="md"
              radius="md"
            />

            <Button
              type="submit"
              fullWidth
              size="md"
              radius="md"
              variant="gradient"
              gradient={{ from: 'indigo', to: 'violet' }}
              loading={isLoading}
            >
              Send Reset Link
            </Button>
          </AnimatedFormWrapper>
        </form>
      )}

      {/* Login Link */}
      <FadeIn delay={900}>
        <Text ta="center" size="sm">
          Remember your password?{' '}
          <Anchor component={Link} to="/auth/login" fw={500}>
            Sign in
          </Anchor>
        </Text>
      </FadeIn>
    </Stack>
  );
}
